import React from 'react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Plus, Trash2 } from 'lucide-react';

export function SectionField({ sectionKey, value, onChange, onBlur, disabled = false }) {
  // Big paragraph
  if (sectionKey === 'summary') {
    return (
      <Textarea
        rows={4}
        value={value || ''}
        onChange={(e) => onChange(e.target.value)}
        onBlur={onBlur}
        readOnly={disabled}
        disabled={disabled}
        placeholder="No summary yet. Click Refine with AI → Enhance to generate one from the resume."
        data-testid={`field-${sectionKey}`}
      />
    );
  }

  // Flat list of strings (languages)
  if (sectionKey === 'languages') {
    return (
      <StringListField value={value} onChange={onChange} onBlur={onBlur} sectionKey={sectionKey} disabled={disabled} />
    );
  }

  // Key Highlights — longer bullets (add / edit / delete)
  if (sectionKey === 'key_highlights') {
    return (
      <BulletsField
        value={value}
        onChange={onChange}
        onBlur={onBlur}
        testIdPrefix="field-key_highlights"
        disabled={disabled}
      />
    );
  }

  // Categorised pairs (skills, tools_technologies)
  if (sectionKey === 'skills') {
    return <SkillsField value={value} onChange={onChange} onBlur={onBlur} disabled={disabled} />;
  }
  if (sectionKey === 'tools_technologies') {
    return <CategorisedField value={value} onChange={onChange} onBlur={onBlur} sectionKey={sectionKey} disabled={disabled} />;
  }

  // Experience (richest)
  if (sectionKey === 'experience') {
    return <ExperienceField value={value} onChange={onChange} onBlur={onBlur} disabled={disabled} />;
  }
  if (sectionKey === 'projects') {
    return <ListOfObjectsField
      value={value} onChange={onChange} onBlur={onBlur}
      fields={[
        { key: 'name', label: 'Project name' },
        { key: 'role', label: 'Role' },
        { key: 'year', label: 'Year' },
        { key: 'summary', label: 'Summary', textarea: true },
      ]}
      newItem={{ name: '', role: '', year: '', summary: '' }}
      sectionKey={sectionKey}
      disabled={disabled}
    />;
  }
  if (sectionKey === 'awards') {
    return <ListOfObjectsField
      value={value} onChange={onChange} onBlur={onBlur}
      fields={[
        { key: 'title', label: 'Title' },
        { key: 'issued_by', label: 'Issued by' },
        { key: 'year', label: 'Year' },
        { key: 'summary', label: 'Note', textarea: true },
      ]}
      newItem={{ title: '', issued_by: '', year: '', summary: '' }}
      sectionKey={sectionKey}
      disabled={disabled}
    />;
  }
  if (sectionKey === 'certifications') {
    return <ListOfObjectsField
      value={value} onChange={onChange} onBlur={onBlur}
      fields={[
        { key: 'name', label: 'Certification' },
        { key: 'issued_by', label: 'Issuer' },
        { key: 'year', label: 'Year' },
      ]}
      newItem={{ name: '', issued_by: '', year: '' }}
      sectionKey={sectionKey}
      disabled={disabled}
    />;
  }
  if (sectionKey === 'education') {
    return <ListOfObjectsField
      value={value} onChange={onChange} onBlur={onBlur}
      fields={[
        { key: 'degree', label: 'Degree' },
        { key: 'institution', label: 'Institution' },
        { key: 'year', label: 'Year' },
        { key: 'highlights', label: 'Highlights', textarea: true },
      ]}
      newItem={{ degree: '', institution: '', year: '', highlights: '' }}
      sectionKey={sectionKey}
      disabled={disabled}
    />;
  }
  if (sectionKey === 'publications') {
    return <ListOfObjectsField
      value={value} onChange={onChange} onBlur={onBlur}
      fields={[
        { key: 'title', label: 'Title' },
        { key: 'venue', label: 'Venue' },
        { key: 'year', label: 'Year' },
        { key: 'url', label: 'URL' },
      ]}
      newItem={{ title: '', venue: '', year: '', url: '' }}
      sectionKey={sectionKey}
      disabled={disabled}
    />;
  }

  // Fallback (shouldn't happen with our 10 known sections)
  return (
    <Textarea
      rows={4}
      value={typeof value === 'string' ? value : JSON.stringify(value, null, 2)}
      onChange={(e) => onChange(e.target.value)}
      onBlur={onBlur}
      readOnly={disabled}
      disabled={disabled}
      className="font-mono text-xs"
      data-testid={`field-${sectionKey}`}
    />
  );
}

// ---------- Generic field components ----------
function StringListField({ value, onChange, onBlur, sectionKey, disabled = false }) {
  const items = Array.isArray(value) ? value : [];
  const update = (next) => onChange(next);
  return (
    <div className="space-y-2">
      <div className="flex flex-wrap gap-1.5">
        {items.map((item, i) => (
          <span
            key={i}
            className="inline-flex items-center gap-1 rounded-md border bg-card px-2 py-1 text-xs"
            data-testid={`chip-${sectionKey}-${i}`}
          >
            <Input
              value={item}
              onChange={(e) => {
                const next = [...items];
                next[i] = e.target.value;
                update(next);
              }}
              onBlur={onBlur}
              readOnly={disabled}
              disabled={disabled}
              className="h-6 w-auto min-w-[80px] border-0 bg-transparent px-1 py-0 shadow-none focus-visible:ring-1"
              aria-label={`${sectionKey} item ${i + 1}`}
            />
            <button
              type="button"
              className="text-muted-foreground hover:text-destructive disabled:pointer-events-none disabled:opacity-50"
              aria-label="Remove"
              disabled={disabled}
              onClick={() => { update(items.filter((_, j) => j !== i)); onBlur(); }}
              data-testid={`chip-remove-${sectionKey}-${i}`}
            >
              <Trash2 className="h-3 w-3" />
            </button>
          </span>
        ))}
      </div>
      <Button
        type="button"
        variant="ghost"
        size="sm"
        className="h-7 gap-1 text-[11px]"
        disabled={disabled}
        onClick={() => { update([...items, '']); }}
        data-testid={`chip-add-${sectionKey}`}
      >
        <Plus className="h-3 w-3" /> Add item
      </Button>
    </div>
  );
}

function SkillsField({ value, onChange, onBlur, disabled = false }) {
  const v = value || { languages: [], platforms: [], practices: [] };
  return (
    <div className="space-y-3">
      {['languages', 'platforms', 'practices'].map((sub) => (
        <div key={sub}>
          <p className="mb-1 text-[11px] font-medium uppercase tracking-wider text-muted-foreground">{sub}</p>
          <StringListField
            value={v[sub] || []}
            onChange={(next) => onChange({ ...v, [sub]: next })}
            onBlur={onBlur}
            sectionKey={`skills-${sub}`}
            disabled={disabled}
          />
        </div>
      ))}
    </div>
  );
}

function CategorisedField({ value, onChange, onBlur, sectionKey, disabled = false }) {
  const items = Array.isArray(value) ? value : [];
  const update = (next) => onChange(next);
  return (
    <div className="space-y-3">
      {items.map((g, i) => (
        <div key={i} className="rounded-lg border bg-secondary/30 p-3" data-testid={`category-${sectionKey}-${i}`}>
          <div className="mb-2 flex items-center gap-2">
            <Input
              value={g.category || ''}
              onChange={(e) => { const next = [...items]; next[i] = { ...g, category: e.target.value }; update(next); }}
              onBlur={onBlur}
              placeholder="Category (e.g. Cloud & DevOps)"
              className="h-7 max-w-xs text-sm font-semibold"
              aria-label="Category name"
              readOnly={disabled}
              disabled={disabled}
            />
            <button
              type="button"
              className="ml-auto text-muted-foreground hover:text-destructive disabled:pointer-events-none disabled:opacity-50"
              onClick={() => { update(items.filter((_, j) => j !== i)); onBlur(); }}
              aria-label="Remove category"
              disabled={disabled}
              data-testid={`category-remove-${sectionKey}-${i}`}
            >
              <Trash2 className="h-3.5 w-3.5" />
            </button>
          </div>
          <StringListField
            value={g.items || []}
            onChange={(next) => { const arr = [...items]; arr[i] = { ...g, items: next }; update(arr); }}
            onBlur={onBlur}
            sectionKey={`${sectionKey}-items-${i}`}
            disabled={disabled}
          />
        </div>
      ))}
      <Button
        type="button"
        variant="ghost"
        size="sm"
        className="h-7 gap-1 text-[11px]"
        disabled={disabled}
        onClick={() => update([...items, { category: '', items: [] }])}
        data-testid={`category-add-${sectionKey}`}
      >
        <Plus className="h-3 w-3" /> Add category
      </Button>
    </div>
  );
}

function ExperienceField({ value, onChange, onBlur, disabled = false }) {
  return (
    <ListOfObjectsField
      value={value}
      onChange={onChange}
      onBlur={onBlur}
      fields={[
        { key: 'role', label: 'Role' },
        { key: 'company', label: 'Company' },
        { key: 'start', label: 'Start (MMM YYYY)' },
        { key: 'end', label: 'End (MMM YYYY or Present)' },
        { key: 'location', label: 'Location' },
        { key: 'bullets', label: 'Impact bullets', type: 'list' },
      ]}
      newItem={{ role: '', company: '', start: '', end: '', location: '', bullets: [] }}
      sectionKey="experience"
      disabled={disabled}
    />
  );
}

function ListOfObjectsField({ value, onChange, onBlur, fields, newItem, sectionKey, disabled = false }) {
  const items = Array.isArray(value) ? value : [];
  const setItem = (i, patch) => {
    const next = [...items];
    next[i] = { ...items[i], ...patch };
    onChange(next);
  };
  return (
    <div className="space-y-3">
      {items.map((item, i) => (
        <div key={i} className="rounded-lg border bg-secondary/30 p-3" data-testid={`item-${sectionKey}-${i}`}>
          <div className="grid grid-cols-1 gap-2 sm:grid-cols-2">
            {fields.filter((f) => f.type !== 'list' && !f.textarea).map((f) => (
              <div key={f.key}>
                <p className="mb-0.5 text-[10px] font-medium uppercase tracking-wider text-muted-foreground">{f.label}</p>
                <Input
                  value={item[f.key] || ''}
                  onChange={(e) => setItem(i, { [f.key]: e.target.value })}
                  onBlur={onBlur}
                  className="h-8 text-sm"
                  aria-label={f.label}
                  readOnly={disabled}
                  disabled={disabled}
                  data-testid={`item-${sectionKey}-${i}-${f.key}`}
                />
              </div>
            ))}
          </div>
          {fields.filter((f) => f.textarea).map((f) => (
            <div key={f.key} className="mt-2">
              <p className="mb-0.5 text-[10px] font-medium uppercase tracking-wider text-muted-foreground">{f.label}</p>
              <Textarea
                rows={2}
                value={item[f.key] || ''}
                onChange={(e) => setItem(i, { [f.key]: e.target.value })}
                onBlur={onBlur}
                className="text-sm"
                aria-label={f.label}
                readOnly={disabled}
                disabled={disabled}
                data-testid={`item-${sectionKey}-${i}-${f.key}`}
              />
            </div>
          ))}
          {fields.filter((f) => f.type === 'list').map((f) => (
            <div key={f.key} className="mt-2">
              <p className="mb-0.5 text-[10px] font-medium uppercase tracking-wider text-muted-foreground">{f.label}</p>
              <BulletsField
                value={item[f.key] || []}
                onChange={(next) => setItem(i, { [f.key]: next })}
                onBlur={onBlur}
                testIdPrefix={`item-${sectionKey}-${i}-${f.key}`}
                disabled={disabled}
              />
            </div>
          ))}
          <Button
            type="button"
            variant="ghost"
            size="sm"
            className="mt-2 h-7 gap-1 text-[11px] text-muted-foreground hover:text-destructive"
            disabled={disabled}
            onClick={() => { onChange(items.filter((_, j) => j !== i)); onBlur(); }}
            data-testid={`item-remove-${sectionKey}-${i}`}
          >
            <Trash2 className="h-3 w-3" /> Remove
          </Button>
        </div>
      ))}
      <Button
        type="button"
        variant="ghost"
        size="sm"
        className="h-7 gap-1 text-[11px]"
        disabled={disabled}
        onClick={() => onChange([...items, { ...newItem }])}
        data-testid={`item-add-${sectionKey}`}
      >
        <Plus className="h-3 w-3" /> Add {fields[0]?.label?.toLowerCase() || 'item'}
      </Button>
    </div>
  );
}

function BulletsField({ value, onChange, onBlur, testIdPrefix, disabled = false }) {
  const items = Array.isArray(value) ? value : [];
  return (
    <div className="space-y-1.5">
      {items.map((b, i) => (
        <div key={i} className="flex items-start gap-2">
          <span className="mt-2 h-1 w-1 shrink-0 rounded-full bg-primary" />
          <Textarea
            rows={1}
            value={b}
            onChange={(e) => { const next = [...items]; next[i] = e.target.value; onChange(next); }}
            onBlur={onBlur}
            className="min-h-[32px] text-sm"
            aria-label={`Bullet ${i + 1}`}
            readOnly={disabled}
            disabled={disabled}
            data-testid={`${testIdPrefix}-${i}`}
          />
          <button
            type="button"
            onClick={() => { onChange(items.filter((_, j) => j !== i)); onBlur(); }}
            className="mt-1 text-muted-foreground hover:text-destructive disabled:pointer-events-none disabled:opacity-50"
            aria-label="Remove bullet"
            disabled={disabled}
            data-testid={`${testIdPrefix}-remove-${i}`}
          >
            <Trash2 className="h-3 w-3" />
          </button>
        </div>
      ))}
      <Button
        type="button"
        variant="ghost"
        size="sm"
        className="h-6 gap-1 text-[11px]"
        disabled={disabled}
        onClick={() => onChange([...items, ''])}
        data-testid={`${testIdPrefix}-add`}
      >
        <Plus className="h-3 w-3" /> Add bullet
      </Button>
    </div>
  );
}
